/**
 * BestPracticeAnalyzerTab Component
 *
 * Provides continuous monitoring against industry frameworks including CISA SCuBA,
 * NIST SP 800-53, Microsoft MCSB, and EIDSCA. Features framework tiles, the
 * failing-controls table with severity tabs and the waiver system, and trends.
 *
 * Formerly "Standards Compliance" - rebranded to better communicate purpose.
 *
 * Carbon layout (frontend-carbon-blueprint §5.2 security-bpa, mockup
 * .agents/Mockups/black-redesign-2026-10/carbon/03-security-center.html):
 *   1. PillarBrief — the action-first verdict (composeBpaBrief), one lede, the
 *      "Fix …" primary to the controls table, the "Configure frameworks"
 *      tertiary, the assessment provenance (D21), and the Best Practice score
 *      tile.
 *   2. Framework tiles in a condensed grid (weighted %, "38 of 50 mapped
 *      controls pass", a ScoreBar in the Best Practice mark color), with the
 *      all-framework and collection-health notes under them.
 *   3. "Failing controls" — the DataTable (id="controls") with severity tabs,
 *      search, Workload / Framework / Show selects and rows that expand in
 *      place into What Guardian found / How to fix it / Frameworks. It also
 *      carries what the deep dive's Violations view used to: the passing and
 *      every-control views, the status and category filters and the
 *      `?policy=` / `?workload=` / `?view=violations` handoffs.
 *   4. The sections below: Top six, workloads by severity, the 30-day trend,
 *      recent control moves, and the By standard / By workload / History deep
 *      dive. A sticky jump bar after the table links them.
 *
 * Part of the Security Center hub.
 */

import { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useMsal } from '@azure/msal-react'
import clsx from 'clsx'
import {
  KeyIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
  ShieldCheckIcon,
  CubeIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PriorityQueue from '@/components/PostureBrief/PriorityQueue'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import { SectionJumpBar } from '@/components/PostureBrief/SectionJumpBar'
import EditorialHeatmap, {
  type Intensity,
  type HeatmapRow,
} from '@/components/PostureBrief/EditorialHeatmap'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'
import EmptyState from '@/components/shared/EmptyState'
import { BigNumber } from '@/components/carbon/BigNumber'
import { Button } from '@/components/carbon/Button'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { ContentSwitcher } from '@/components/carbon/ContentSwitcher'
import { DataTable, type DataColumn } from '@/components/carbon/DataTable'
import { IconButton } from '@/components/carbon/IconButton'
import { IconCheck, IconSliders, IconWarning } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { LineTabs } from '@/components/carbon/LineTabs'
import { ScoreBar } from '@/components/carbon/ScoreBar'
import { SegmentedMeter } from '@/components/carbon/SegmentedMeter'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Sparkline } from '@/components/carbon/Sparkline'
import { StructuredList } from '@/components/carbon/StructuredList'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { TrendChart } from '@/components/carbon/TrendChart'
import type { GridColumnCount } from '@/components/carbon/types'
import { composeBpaBrief } from '@/lib/security-center/bpa-brief'
import {
  compute7dFrameworkDelta,
  frameworkSparkline,
} from '@/lib/security-center/framework-trend'
import { formatTriggeredBy } from '@/lib/userDisplay'
import { getServerMessage } from '@/lib/api-error'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import {
  ViolationsTable,
  StandardsConfigModal,
  WaiverModal,
  GroupedPoliciesDisplay,
  FrameworkControlsList,
  NistDisclaimer,
} from '@/components/Compliance'
import { assessmentProvenance } from '@/components/Compliance/assessment-provenance'
import {
  calculateStarredBpaScore,
  useComplianceDashboard,
  useComplianceScoreHistory,
  useControlChanges,
  useCreateWaiver,
  useUpdateWaiver,
  useDeleteWaiver,
  useBulkUpdateTenantStandards,
  useToggleIncludeInScore,
} from '@/hooks/useComplianceData'
import type {
  ComplianceViolation,
  ControlChange,
  CreateWaiverRequest,
  UpdateWaiverRequest,
  UpdateStandardConfigRequest,
  M365Workload,
  FrameworkScoreDisplay,
  ViolationStatus,
  WorkloadCollectionHealth,
} from '@/types'
import { M365_WORKLOADS, getWorkloadForProduct } from '@/types'

export interface BestPracticeAnalyzerTabRef {
  /** Refresh the current compliance data */
  refresh: () => void
}

export interface BestPracticeAnalyzerTabProps {
  /** The tenant ID to analyze */
  tenantId: string | undefined
  /** Callback when the overall score changes */
  onScoreChange?: (score: number | null) => void
}

/**
 * Deep-dive views. `violations` is no longer a deep-dive tab: the failing-
 * controls table near the top holds that view, and `?view=violations` scrolls
 * to it.
 */
type InternalTabKey = 'by-standard' | 'by-workload' | 'violations' | 'history'
type DeepDiveKey = Exclude<InternalTabKey, 'violations'>

interface InternalTabItem {
  key: DeepDiveKey
  name: string
}

const internalTabs: InternalTabItem[] = [
  { key: 'by-standard', name: 'By standard' },
  { key: 'by-workload', name: 'By workload' },
  { key: 'history', name: 'History' },
]

/**
 * Preferred display ordering for frameworks across Overview and By Standard tabs.
 * MCSB first because it is always included in the Guardian Score.
 */
import { FRAMEWORK_ORDER } from '@/lib/frameworkUtils'
export { FRAMEWORK_ORDER }

/** Framework blurbs - concise descriptions for each standard heading */
export const FRAMEWORK_BLURBS: Record<string, string> = {
  'MCSB': 'Microsoft-maintained security baseline for cloud services covering identity, networking, data protection, and more.',
  'EIDSCA': 'Community-driven checks for Entra ID (Azure AD) security settings, conditional access, and authentication policies.',
  'NIST_800_53': 'Federal security controls framework mapping M365 configurations to NIST SP 800-53 Rev 5 requirements.',
  'SCUBA': 'CISA\'s Secure Cloud Business Applications project with prescriptive M365 security configuration baselines.',
  'CIS': 'CIS Microsoft 365 Foundations Benchmark v5.0.0 with prescriptive security configuration recommendations across all M365 services.',
}

import { refMatchesFramework } from '@/lib/frameworkUtils'

// Workload icons. Neutral (the icon-secondary tone): a workload is not a
// pillar, so it takes no hue (DESIGN.md "The Category-Only Rule").
const WORKLOAD_DISPLAY: Record<M365Workload, {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}> = {
  Identity: { icon: KeyIcon },
  Exchange: { icon: EnvelopeIcon },
  Teams: { icon: ChatBubbleLeftRightIcon },
  SharePoint: { icon: FolderIcon },
  Defender: { icon: ShieldCheckIcon },
  Other: { icon: CubeIcon },
}

/** Whether a workload is missing any of its EXPECTED data sources. */
function hasIncompleteData(health: WorkloadCollectionHealth | undefined): boolean {
  if (!health) return false
  return (
    (health.health.graphExpected && !health.health.graphCollected) ||
    (health.health.powerShellExpected && !health.health.powerShellCollected)
  )
}

/**
 * Health tag for workload data collection status.
 * "Data collected" when all EXPECTED data sources were collected, "Partial
 * data" when some were, "No data collected" when none were. Neutral tags
 * (the Neutral Bad State Rule); the glyph and the word carry the state.
 * Respects graphExpected/powerShellExpected so workloads like Identity
 * (Graph-only) don't permanently show "Partial".
 */
function HealthBadge({ health }: { health: WorkloadCollectionHealth }) {
  const { graphCollected, powerShellCollected, graphExpected, powerShellExpected } = health.health
  // "Healthy" = all expected data sources were collected
  const allCollected = (graphCollected || !graphExpected) && (powerShellCollected || !powerShellExpected)
  // "No data" = none of the expected sources were collected
  const noneCollected = (graphExpected && !graphCollected) && (powerShellExpected ? !powerShellCollected : false)
  // For single-source workloads, check only the expected source
  const singleSourceNone = !allCollected && ((graphExpected && !graphCollected && !powerShellExpected) ||
                                              (powerShellExpected && !powerShellCollected && !graphExpected))

  if (allCollected) {
    return (
      <Tag size="sm" icon={<IconCheck />}>
        Data collected
      </Tag>
    )
  }

  if (noneCollected || singleSourceNone) {
    return (
      <Tag size="sm" kind="high-contrast" icon={<IconWarning />}>
        No data collected
      </Tag>
    )
  }

  return (
    <Tag size="sm" icon={<IconWarning />}>
      Partial data
    </Tag>
  )
}

/* -------------------------------------------------------------------------- */
/* Pure helpers — module scope so they stay testable without mounting.         */
/* -------------------------------------------------------------------------- */

/**
 * Heatmap intensity ramp for critical (SHALL) counts. Even one open critical
 * is the strongest neutral; three or more is the Coral critical bucket
 * (`crit`). The BPA tab and any future workload heatmap agree on the ramp.
 */
function intensityFromShall(n: number): Intensity {
  if (n === 0) return 'l0'
  if (n <= 2) return 'l3'
  return 'l4'
}

/** High (SHOULD) ramp — neutral at every level. */
function intensityFromShould(n: number): Intensity {
  if (n === 0) return 'l0'
  if (n <= 2) return 'l2'
  if (n <= 5) return 'l3'
  return 'l4'
}

/** Medium (MAY) ramp — stays light even at high counts. */
function intensityFromMay(n: number): Intensity {
  if (n === 0) return 'l0'
  if (n <= 5) return 'l1'
  return 'l2'
}

/**
 * Extract the framework code from a BPA priority-action id. Action ids are
 * `bpa:<policyCode>` where policyCode is `<framework>.<...>` (e.g.
 * `SCuBA.AAD.4.1v1`, `EIDSCA.PSO.01`, `MCSB.IM-1`). Returns the leading
 * framework code, or `null` if the id is malformed.
 */
function extractFrameworkCodeFromActionId(id: string): string | null {
  if (!id.startsWith('bpa:')) return null
  const rest = id.slice('bpa:'.length)
  const dot = rest.indexOf('.')
  if (dot <= 0) return null
  return rest.slice(0, dot)
}

/**
 * Trailing-clause verbs for the "Recent control moves" feed (§6.5). Keyed by
 * `ControlChange.changeKind`. Period-then-clause style, no em-dashes (CLAUDE.md
 * voice rules). Each value is rendered after the policy title, e.g.
 * "Conditional Access. Started failing."
 */
const CONTROL_CHANGE_TRAILING_CLAUSE: Record<ControlChange['changeKind'], string> = {
  Improved: 'Came into compliance.',
  Regressed: 'Started failing.',
  Waived: 'Was waived.',
  WaiverLapsed: 'Is failing again. Waiver lapsed.',
  WaiverModified: 'Waiver was updated.',
}

/**
 * Threshold for upgrading a Waived row from the plain word to an expiry tag.
 * Matches the warning window used elsewhere in the BPA flow (the daily sweep
 * runs once per 24h, so 14 days gives the user roughly two weekly cadences
 * of warning before the waiver lapses out from under them).
 */
const WAIVER_EXPIRY_WARNING_DAYS = 14

/**
 * "Recent control moves" feed (§6.5) — display window + collapse cap.
 *
 * The backend returns the most-recent flips all-time (no date filter), so on a
 * long-lived tenant the feed accretes into an unbounded list that reads as
 * bloat. We bound the *display* to a rolling 30-day window — matching the §6.4
 * composite-trend window so "lately" means the same thing across the page — and
 * collapse it to the first 5 rows, with an opt-in expander for the rest of the
 * window. Client-side filtering is honest here: the server's top-20 cap sits
 * well above the flip volume these tenants produce in a month, so this trims
 * older rows rather than hiding recent ones.
 */
const MOVES_WINDOW_DAYS = 30
const MOVES_VISIBLE_CAP = 5

// Sticky offset for in-page anchors below the jump bar: the pillar tabs plus
// the bar. The frameworks and the controls table sit above the bar, so only
// the tabs cover them.
const SCROLL_MT = 'scroll-mt-[calc(var(--pillar-tabs-h)+4rem)]'
const SCROLL_MT_TOP = 'scroll-mt-[calc(var(--pillar-tabs-h)+1rem)]'

/**
 * Formats the expiry copy on the expiry tag — relative ("in 3 days") inside
 * the warning window, absolute ("May 24") at exactly the threshold. Keeps
 * the tag narrow and lets the user scan urgency.
 */
function formatWaiverExpiryCopy(expiresAt: Date, now: Date): string {
  const msPerDay = 86_400_000
  const days = Math.round((expiresAt.getTime() - now.getTime()) / msPerDay)
  if (days <= 0) return 'Expires today'
  if (days === 1) return 'Expires in 1 day'
  if (days <= 7) return `Expires in ${days} days`
  return `Expires ${expiresAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
}

/**
 * Right-slot tag for a `Waived`-kind move row. Branches on the backend's
 * waiver expiry fields:
 *   * Permanent / >14d out → plain "Waived" (no expiry context to show).
 *   * <=14d to expiry, not yet expired → "Expires in N days" tag.
 *   * ExpiresAt in the past (sweep pending) → high-contrast "Waiver expired"
 *     (same treatment as the `WaiverLapsed` branch below).
 *   * No active waiver remains (`waiverIsExpired == null` AND
 *     `waiverExpiresAt == null` but the row is Waived) → "Waived (lapsed)",
 *     signalling the waiver was deactivated/deleted post-emit.
 */
function renderWaivedRightSlot(change: ControlChange): React.ReactNode {
  const expiresAtRaw = change.waiverExpiresAt
  const isExpired = change.waiverIsExpired

  if (isExpired === true) {
    return (
      <Tag size="sm" kind="high-contrast" title="Waiver past its expiry. The next waiver sweep closes it.">
        Waiver expired
      </Tag>
    )
  }

  if (expiresAtRaw) {
    const expiresAt = new Date(expiresAtRaw)
    if (!Number.isNaN(expiresAt.getTime())) {
      const now = new Date()
      const msToExpiry = expiresAt.getTime() - now.getTime()
      const daysToExpiry = msToExpiry / 86_400_000
      if (daysToExpiry <= WAIVER_EXPIRY_WARNING_DAYS) {
        return (
          <Tag size="sm" title={`Waiver expires ${expiresAt.toLocaleString()}`}>
            {formatWaiverExpiryCopy(expiresAt, now)}
          </Tag>
        )
      }
    }
  }

  // Backend signals "no active waiver" with waiverIsExpired === null (vs
  // false for Permanent / future-dated, which renders as plain "Waived").
  // Surface the deactivation so the user can rule out drift confusion.
  const noActiveWaiver = isExpired == null
  return (
    <span className="whitespace-nowrap text-helper text-text-helper">
      {noActiveWaiver ? 'Waived (lapsed)' : 'Waived'}
    </span>
  )
}

/**
 * Right-column renderer for a "Recent control moves" row (§6.5).
 *   * `Waived` and `WaiverModified` both read as plain "Waived" by default; if
 *     the backend has surfaced waiverExpiresAt within 14 days we upgrade to
 *     an "Expires in N days" tag, and if the active waiver is already past
 *     expiry (sweep pending) the high-contrast "Waiver expired" tag. No active
 *     waiver in DB → "Waived (lapsed)". `WaiverModified` shares this path
 *     because `DecorateWaivedRowsWithExpiryAsync` populates the same fields on
 *     those rows.
 *   * `WaiverLapsed` always renders the "Waiver expired" tag.
 *   * Unweighted policies (`scoreDelta == null`) render "Not scored".
 *   * Everything else uses ScoreDeltaChip (which owns the sign and the 0.05
 *     noise threshold).
 */
function renderControlChangeRightSlot(change: ControlChange): React.ReactNode {
  if (change.changeKind === 'Waived' || change.changeKind === 'WaiverModified') {
    return renderWaivedRightSlot(change)
  }
  if (change.changeKind === 'WaiverLapsed') {
    return (
      <Tag size="sm" kind="high-contrast" title="Waiver has lapsed">
        Waiver expired
      </Tag>
    )
  }
  if (change.scoreDelta == null) {
    return <span className="whitespace-nowrap text-helper text-text-helper">Not scored</span>
  }
  return <ScoreDeltaChip delta={change.scoreDelta} />
}

/* -------------------------------------------------------------------------- */
/* Framework tiles (mockup 03 `.fws`)                                          */
/* -------------------------------------------------------------------------- */

/** A 16px star with square-cut miter joins (the Carbon icon stroke rules). */
function StarGlyph({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      aria-hidden="true"
      focusable="false"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinejoin="miter"
    >
      <path d="m12 3 2.7 5.6 6.1.8-4.5 4.2 1.1 6.1L12 16.8l-5.4 2.9 1.1-6.1-4.5-4.2 6.1-.8z" />
    </svg>
  )
}

/**
 * Framework tiles a row at c-xlg: all of them up to four, then three or four,
 * whichever leaves fewer empty cells (an empty cell shows the grid's gutter
 * color as a block).
 */
function wideTileColumns(n: number): GridColumnCount {
  if (n <= 4) return Math.max(1, n) as GridColumnCount
  const empty = (cols: number) => (cols - (n % cols)) % cols
  return empty(3) < empty(4) ? 3 : 4
}

/** "+3 in 7 days", "No change in 7 days"; the sign carries the direction (no hue). */
function delta7dText(delta: number | null): string | null {
  if (delta == null) return null
  if (delta === 0) return 'No change in 7 days'
  return `${delta > 0 ? '+' : '−'}${Math.abs(delta)} in 7 days`
}

/**
 * Framework tile (mockup 03): the name, the weighted pass rate as a 2rem light
 * figure, the "38 of 50 mapped controls pass" helper line, and an 8px bar in
 * the Best Practice mark color. Below it, the 7-day change, the 14-day line
 * and the link into the deep dive. The star toggles the framework's place in
 * the Guardian Score (MCSB is always in it).
 */
function FrameworkTile({
  framework,
  delta7d,
  sparkline,
  onToggleStar,
  onOpen,
}: {
  framework: FrameworkScoreDisplay
  delta7d: number | null
  sparkline: number[]
  onToggleStar: () => void
  onOpen: () => void
}) {
  const isAlwaysIncluded = (framework.code ?? '').toUpperCase() === 'MCSB'
  const starred = framework.includeInGuardianScore || isAlwaysIncluded
  const score = Math.round(framework.score ?? 0)
  const delta = delta7dText(delta7d)
  return (
    <Tile as="li" className="flex min-w-0 flex-col pb-5">
      <TileHeader
        title={framework.name}
        level={3}
        aside={
          <IconButton
            size={32}
            icon={<StarGlyph filled={starred} />}
            onClick={onToggleStar}
            disabled={isAlwaysIncluded}
            aria-pressed={starred}
            label={
              isAlwaysIncluded
                ? `${framework.code} is always included in the Guardian Score`
                : starred
                  ? `Remove ${framework.code} from Guardian Score`
                  : `Include ${framework.code} in Guardian Score`
            }
            title={
              isAlwaysIncluded
                ? 'MCSB is always included in your Guardian Score'
                : starred
                  ? 'Remove from your Guardian Score'
                  : 'Include in your Guardian Score'
            }
            className="-my-2 -mr-2 text-text-primary"
          />
        }
      />
      <BigNumber value={score} suffix="%" size="figure" className="mt-3" />
      <p className="m-0 mt-0.5 text-helper tabular-nums text-text-helper">
        {framework.passedControls} of {framework.totalControls} mapped controls pass
      </p>
      <ScoreBar value={score} category="bpa" label={`${framework.name} pass rate`} className="mt-4" />
      {/* Wraps rather than truncating the change tag when four tiles share a row. */}
      <div className="mt-4 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        {delta ? (
          <Tag size="sm" className="tabular-nums">
            {delta}
          </Tag>
        ) : (
          <span className="text-helper text-text-helper">No 7-day change yet</span>
        )}
        {sparkline.length >= 2 && (
          <Sparkline points={sparkline} height={24} className="w-20 flex-none" />
        )}
        <Button
          kind="ghost"
          size="sm"
          onClick={onOpen}
          aria-label={`Open ${framework.name} in the deep dive`}
          className="-mr-3 ml-auto"
        >
          Open
        </Button>
      </div>
    </Tile>
  )
}

/**
 * The frameworks NOT in the Guardian Score (whether already evaluated or not),
 * as one strip under the tiles: each name and its Enable action. Score, trend
 * and the deep-dive link stay hidden; backend evaluation keeps running so the
 * reveal on Enable is instant. A strip, not tiles, so the tile row holds only
 * scored frameworks (mockup 03) and never leaves empty grid cells.
 */
function OutsideScoreStrip({
  frameworks,
  attached,
  enablingId,
  onEnable,
}: {
  frameworks: { standardId: string; name: string }[]
  /** Sits flush under the tile grid (shares its border). */
  attached: boolean
  enablingId: string | null
  onEnable: (standardId: string) => void
}) {
  return (
    <div
      className={clsx(
        'flex min-w-0 flex-wrap items-center gap-x-6 gap-y-3 border border-line-subtle bg-layer-1 px-4 py-3',
        attached && 'border-t-0',
      )}
    >
      <p className="m-0 text-helper text-text-helper">Not in your Guardian Score. Enable one to drive your score.</p>
      <ul className="m-0 flex min-w-0 list-none flex-wrap items-center gap-x-6 gap-y-3 p-0" aria-label="Frameworks not in your Guardian Score">
        {frameworks.map((f) => {
          const pending = enablingId === f.standardId
          return (
            <li key={f.standardId} className="flex min-w-0 items-center gap-3">
              <span className="min-w-0 text-body text-text-secondary">{f.name}</span>
              <Button kind="tertiary" size="sm" onClick={() => onEnable(f.standardId)} loading={pending} aria-label={`Enable ${f.name}`}>
                {pending ? 'Enabling' : 'Enable'}
              </Button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Format a signed delta as "+3.2" / "−1.4" / "0". */
function formatSignedDelta(n: number): string {
  if (n === 0) return '0'
  return n > 0 ? `+${n}` : `−${Math.abs(n)}`
}

/** A KPI tile under the 30-day chart: a helper label over a 2rem light figure. */
function TrendKpi({ label, value }: { label: string; value: string }) {
  return (
    <Tile as="li" className="min-w-0">
      <p className="m-0 text-helper text-text-helper">{label}</p>
      <p className="m-0 mt-1 text-figure font-light tabular-nums text-text-primary">{value}</p>
    </Tile>
  )
}

/** A section heading with its one-line description. */
function SectionHead({ id, title, children }: { id: string; title: string; children?: React.ReactNode }) {
  return (
    <div className="mb-6">
      <div id={id}>
        <SectionHeadline level="page">{title}</SectionHeadline>
      </div>
      {children ? <p className="m-0 mt-1 max-w-[72ch] text-body text-text-secondary">{children}</p> : null}
    </div>
  )
}

/**
 * BestPracticeAnalyzerTab - M365 security configuration checks based on industry frameworks.
 *
 * Evaluates tenant against checks derived from:
 * - CISA SCuBA: Secure Cloud Business Applications
 * - NIST SP 800-53: Security and Privacy Controls
 * - Microsoft MCSB: Microsoft Cloud Security Benchmark
 * - EIDSCA: Entra ID Security Configuration Analyzer
 *
 * Note: These are built-in checks for common security configurations. For
 * comprehensive compliance assessment, pair with Microsoft Purview Compliance Manager.
 */
const BestPracticeAnalyzerTab = forwardRef<
  BestPracticeAnalyzerTabRef,
  BestPracticeAnalyzerTabProps
>(function BestPracticeAnalyzerTab({ tenantId, onScoreChange }, ref) {
  // Modal state
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const [isWaiverModalOpen, setIsWaiverModalOpen] = useState(false)
  const [selectedViolation, setSelectedViolation] = useState<ComplianceViolation | null>(null)

  // Deep-dive view (controlled so we can switch programmatically)
  const [activeInternalTab, setActiveInternalTab] = useState<DeepDiveKey>('by-standard')

  // Workload filter for the failing-controls table
  const [selectedWorkloadFilter, setSelectedWorkloadFilter] = useState<M365Workload | 'all'>('all')

  // Per-framework view mode for By Standard tab: 'issues' (default) or 'controls'
  const [frameworkViewMode, setFrameworkViewMode] = useState<Record<string, 'issues' | 'controls'>>({})

  // standardId of the framework whose Enable click is currently in flight.
  // Drives the per-tile "Enabling" state on DisabledFrameworkTile so the
  // pending indicator is scoped to the tile the user clicked, not every
  // disabled tile on the page.
  const [enablingStandardId, setEnablingStandardId] = useState<string | null>(null)

  // Phase B §6.1 — Top-six priority queue framework filter state.
  // Pure client-side filter on action.id (which carries the policy code, e.g.
  // `bpa:SCuBA.AAD.4.1v1`). Default = "all" (cross-framework view).
  const [bpaFrameworkFilter, setBpaFrameworkFilter] = useState<string>('all')

  // Top-six → controls-table focus handoff. Cleared by ViolationsTable via
  // onFocusConsumed after the scroll fires, which lets a second click on the
  // same row re-trigger.
  const [focusViolationCode, setFocusViolationCode] = useState<string | null>(null)
  const handleFocusConsumed = useCallback(() => setFocusViolationCode(null), [])

  // §6.5 — Recent control moves: collapsed to MOVES_VISIBLE_CAP rows by default;
  // the expander reveals the rest of the 30-day window on demand.
  const [movesExpanded, setMovesExpanded] = useState(false)

  /** Scroll an in-page section into view once the current render commits. */
  const scrollToId = useCallback((id: string) => {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [])

  // Deep-link scroll target held until the sections exist: on a cold load the
  // skeleton renders first, so an immediate scroll would find nothing.
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null)

  // External entry handoff: Command Center pillar tiles and the "What to do
  // this week" queue deep-link with `?policy=<code>`. The Operations heatmap
  // also deep-links with `?workload=<wl>` to land here filtered by workload.
  // On mount we apply the matching focus (policy row) or filter (workload) on
  // the failing-controls table, and strip the param so a refresh doesn't
  // re-trigger. Wrapping the URL read in useSearchParams keeps it reactive
  // for in-page param changes (e.g. user back/forward).
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const policyParam = searchParams.get('policy')
    const rawWorkloadParam = searchParams.get('workload')
    const workloadParam = M365_WORKLOADS.find(w => w === rawWorkloadParam) ?? null
    if (!policyParam && !workloadParam) return
    if (policyParam) {
      setSelectedWorkloadFilter('all')
      setFocusViolationCode(policyParam)
    } else if (workloadParam) {
      // 'Other' isn't a Workload option on the table — fall back to 'all'
      // rather than silently filter to nothing.
      setSelectedWorkloadFilter(workloadParam === 'Other' ? 'all' : workloadParam)
      setPendingScrollId('controls')
    }
    // Drop just the consumed params; preserve `tab=best-practices` and any
    // other unrelated query state.
    setSearchParams(
      prev => {
        prev.delete('policy')
        prev.delete('workload')
        return prev
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  // U-F3c — `?view=<sub-tab>` deep-link for the deep-dive nav. Pairs with
  // `#deep-dive` anchors emitted by the SectionJumpBar so external surfaces
  // (Command Center pillar tiles, share links) can land users on a specific
  // view. Param is left in the URL — unlike the `policy` / `workload` handoffs
  // above this isn't a one-shot focus, it's the addressable view itself.
  // `violations` now lives in the failing-controls table, so it scrolls there.
  useEffect(() => {
    const viewParam = searchParams.get('view') as InternalTabKey | null
    if (!viewParam) return
    if (viewParam === 'violations') {
      setPendingScrollId('controls')
      return
    }
    if (internalTabs.some(t => t.key === viewParam)) setActiveInternalTab(viewParam as DeepDiveKey)
  }, [searchParams])

  // Section 03 heatmap → By Workload deep-dive handoff. `tick` increments on
  // every click so re-clicks remount the targeted GroupedPoliciesDisplay and
  // re-open the requested severity bucket even after a manual collapse.
  const [openWorkloadGroup, setOpenWorkloadGroup] = useState<{
    workload: M365Workload | null
    group: 'critical' | 'high' | 'medium' | null
    tick: number
  }>({ workload: null, group: null, tick: 0 })

  // Heatmap WAIVED column → failing-controls table handoff. Bumping `tick`
  // remounts ViolationsTable with the requested status filter applied,
  // mirroring the openWorkloadGroup pattern above.
  const [violationsTableSignal, setViolationsTableSignal] = useState<{
    status: ViolationStatus | 'all'
    tick: number
  }>({ status: 'all', tick: 0 })

  // React Query hooks for data fetching - using the dashboard hook
  const {
    dashboardData,
    collectionHealth,
    summary,
    violations,
    assignments,
    trendData: trends,
    isLoading,
    isError,
    refetch: refetchAll,
  } = useComplianceDashboard(tenantId)

  // 30-day score history feeds the framework sparklines (§6.2 — sliced to the
  // last 14 days) and the composite trend chart (§6.4). One fetch, one cache
  // entry — derived slices live in useMemo below.
  const { data: scoreHistory30d } = useComplianceScoreHistory(tenantId, 30)

  // §6.5 — Per-control flip feed (replaces the per-eval rollup; see
  // per-control-change-feed.md). Each row is one policy that transitioned
  // Pass↔Fail or was waived since the prior eval, with the signed GS delta.
  const {
    data: controlChanges,
    isError: controlChangesError,
    refetch: refetchControlChanges,
  } = useControlChanges(tenantId, 20)

  // Bound the feed to a rolling 30-day window (see MOVES_WINDOW_DAYS). Returns
  // the source value untouched while loading (undefined) so the section's
  // loading branch still short-circuits to null.
  const windowedControlChanges = useMemo(() => {
    if (!controlChanges) return controlChanges
    const cutoff = Date.now() - MOVES_WINDOW_DAYS * 24 * 60 * 60 * 1000
    return controlChanges.filter((c) => new Date(c.detectedAt).getTime() >= cutoff)
  }, [controlChanges])

  // Collapse to the first MOVES_VISIBLE_CAP rows unless the user has expanded.
  const visibleControlChanges = useMemo(() => {
    if (!windowedControlChanges) return windowedControlChanges
    return movesExpanded ? windowedControlChanges : windowedControlChanges.slice(0, MOVES_VISIBLE_CAP)
  }, [windowedControlChanges, movesExpanded])

  // Phase B §6.1 — Server-ranked top-6 BPA priority actions (curated SHALL
  // violations across starred frameworks). The hook takes the pillar filter
  // server-side; framework-level filtering happens client-side via
  // `bpaFrameworkFilter` so the filter tags don't refetch.
  const { actions: bpaPriorityActions, isLoading: bpaPriorityActionsLoading } = usePriorityActions({
    tenantId,
    maxCount: 6,
    pillar: 'bpa',
  })

  // Mutations
  const createWaiverMutation = useCreateWaiver()
  const updateWaiverMutation = useUpdateWaiver()
  const deleteWaiverMutation = useDeleteWaiver()
  const bulkUpdateStandardsMutation = useBulkUpdateTenantStandards()
  const toggleIncludeInScoreMutation = useToggleIncludeInScore()

  // Headline "Best Practice Score" — the weighted average of the frameworks the
  // tenant has opted into the Guardian Score (★), via the SAME helper the
  // Command Center BPA pillar, the SecurityCenter composite, and the posture
  // brief all use (SecurityCenter.tsx, PillarBpa.tsx). This page used to
  // lead with `summary.overallScore`, which averages *every* assigned framework
  // (★ or not), so the analyzer headline disagreed with the pillar whenever an
  // unstarred framework was assigned (e.g. N4SW: 28% starred vs 22% all). The
  // all-framework average is preserved as a labeled secondary stat below.
  const starredBpaScore = useMemo(() => {
    const frameworks = dashboardData?.frameworks
    if (!frameworks) return null
    return calculateStarredBpaScore(
      frameworks.map(f => ({
        code: f.code,
        score: f.score,
        weight: f.weight,
        includeInGuardianScore: f.includeInGuardianScore,
      })),
    )
  }, [dashboardData?.frameworks])

  // Frameworks assigned but NOT in the Guardian Score. This unstarred set is
  // exactly what makes `overallScore` (all-framework avg) diverge from the
  // headline — used to gate and label the secondary stat so it only shows when
  // the two numbers actually differ. Mirrors `calculateStarredBpaScore`
  // (flag-only) so the count reconciles with the headline.
  const unstarredFrameworkCount = useMemo(
    () => (dashboardData?.frameworks ?? []).filter(f => !f.includeInGuardianScore).length,
    [dashboardData?.frameworks],
  )

  // Notify parent when score changes. Reports the starred headline so a parent
  // badge/sticky readout matches the score tile (not the all-framework average).
  useEffect(() => {
    if (onScoreChange) {
      onScoreChange(starredBpaScore)
    }
  }, [starredBpaScore, onScoreChange])

  // Brief — composer-driven verdict at the top of the page.
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username
  const brief = useMemo(() => {
    const frameworks = (dashboardData?.frameworks ?? []).map((f: FrameworkScoreDisplay) => ({
      code: f.code,
      score: f.score ?? 0,
      starred: f.includeInGuardianScore,
      isAlwaysIncluded: (f.code ?? '').toUpperCase() === 'MCSB',
    }))

    const starred = frameworks.filter(f => f.starred || f.isAlwaysIncluded)
    const weakestFramework = starred.length > 0
      ? starred.reduce((min, f) => (f.score < min.score ? f : min))
      : undefined

    // `violations` is paged at 500 rows (see useComplianceDashboard), so a
    // client-side filter().length here undercounts once a tenant has more
    // than 500 open findings. The counts come from the violations summary
    // endpoint instead — accurate, unpaged and scoped to starred frameworks,
    // the same scope the composite score uses. Both are `undefined` (unknown)
    // unless the summary or the list query succeeded (`totalViolationsKnown`),
    // never a fabricated 0 (blueprint §5.2 wiring note).
    const known = dashboardData?.totalViolationsKnown === true
    const openShallCount = known ? dashboardData?.criticalViolations : undefined
    const openCount = known ? dashboardData?.totalViolations : undefined

    // Source the #1 action from the same server-ranked queue that powers the
    // "Top six to fix first" table below. Picking the first SHALL out
    // of `violations` would land on whatever order the API returns and cause
    // the brief to disagree with the queue.
    const topRanked = bpaPriorityActions[0]
    const topAction = topRanked
      ? {
          title: topRanked.title,
          framework: extractFrameworkCodeFromActionId(topRanked.id) ?? undefined,
        }
      : undefined

    return composeBpaBrief({
      userEmail,
      tenantId,
      // Starred composite, not overallScore — the brief contract (bpa-brief.ts
      // §5-7) bands the verdict off the BPA composite and names the weakest
      // *starred* framework, so it must reason over the same number the score
      // tile shows.
      bpaScore: starredBpaScore,
      frameworks,
      openShallCount,
      openCount,
      topAction,
      // The control table on this tab. The search is spelled out because a
      // hash-only router link drops it, which would switch to the Overview tab.
      primaryHref: '?tab=best-practices#controls',
      weakestFramework: weakestFramework
        ? { code: weakestFramework.code, score: weakestFramework.score }
        : undefined,
      delta30d: null,
    })
  }, [
    starredBpaScore,
    dashboardData?.frameworks,
    dashboardData?.criticalViolations,
    dashboardData?.totalViolations,
    dashboardData?.totalViolationsKnown,
    bpaPriorityActions,
    userEmail,
    tenantId,
  ])

  // Phase B §6.1 — Filter tags for the top-six priority queue. We derive the
  // available framework codes from the actions themselves (so tags reflect
  // what's actually in the queue) plus the "All" option. Each action's id is
  // shaped `bpa:<policyCode>` (e.g. `bpa:SCuBA.AAD.4.1v1`); the framework
  // segment is the first dotted token after the prefix.
  const availableBpaFrameworkChips = useMemo(() => {
    const codes = new Set<string>()
    for (const action of bpaPriorityActions) {
      const code = extractFrameworkCodeFromActionId(action.id)
      if (code) codes.add(code)
    }
    // Stable display order: prefer the FRAMEWORK_ORDER from frameworkUtils so
    // tag ordering matches the rest of the page; unknowns sort last.
    const ordered = Array.from(codes).sort((a, b) => {
      const oa = FRAMEWORK_ORDER[a] ?? 99
      const ob = FRAMEWORK_ORDER[b] ?? 99
      if (oa !== ob) return oa - ob
      return a.localeCompare(b)
    })
    return ['all', ...ordered]
  }, [bpaPriorityActions])

  // Apply the tag filter to the server-ranked actions. Filtering on `id` is
  // the only honest signal — `meta` carries the product, not the framework.
  const filteredBpaPriorityActions = useMemo(() => {
    if (bpaFrameworkFilter === 'all') return bpaPriorityActions
    return bpaPriorityActions.filter(a => {
      const code = extractFrameworkCodeFromActionId(a.id)
      return code != null && code.toUpperCase() === bpaFrameworkFilter.toUpperCase()
    })
  }, [bpaPriorityActions, bpaFrameworkFilter])

  // Phase B/C §6.2 — Framework tiles. Each tile carries current score, ★
  // inclusion, 7d delta, 14d sparkline series, and pass/total counts. The
  // 14d slice is derived from the 30d query above.
  const frameworkCardData = useMemo(() => {
    const fws = dashboardData?.frameworks ?? []
    const history14d = (scoreHistory30d ?? [])
      .slice()
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-14)
    return fws
      .filter(f => !f.isGuardian)
      .sort((a, b) => {
        const oa = FRAMEWORK_ORDER[a.code] ?? 99
        const ob = FRAMEWORK_ORDER[b.code] ?? 99
        return oa - ob
      })
      .map(f => ({
        framework: f,
        delta7d: compute7dFrameworkDelta(history14d, f.code, f.score ?? 0),
        sparkline: frameworkSparkline(history14d, f.code),
      }))
  }, [dashboardData?.frameworks, scoreHistory30d])

  // "Available · not yet starred" tiles — frameworks present in assignments
  // but not yet resolved on `dashboardData.frameworks`. Same logic as
  // <FrameworkRecommendation>.
  const unstarredAvailableFrameworks = useMemo(() => {
    const knownCodes = new Set(
      (dashboardData?.frameworks ?? []).map(f => (f.code ?? '').toUpperCase()),
    )
    return assignments
      .filter(a => !knownCodes.has((a.standardCode ?? '').toUpperCase()))
      .map(a => ({ code: a.standardCode, name: a.standardName, standardId: a.standardId }))
  }, [assignments, dashboardData?.frameworks])

  // The tile row holds the frameworks in the Guardian Score (MCSB always);
  // the rest, evaluated or only assigned, go to the strip under it.
  const scoredFrameworkTiles = useMemo(
    () =>
      frameworkCardData.filter(
        ({ framework }) => framework.includeInGuardianScore || (framework.code ?? '').toUpperCase() === 'MCSB',
      ),
    [frameworkCardData],
  )
  const outsideScoreFrameworks = useMemo(
    () => [
      ...frameworkCardData
        .filter(({ framework }) => !scoredFrameworkTiles.some((s) => s.framework.standardId === framework.standardId))
        .map(({ framework }) => ({ standardId: framework.standardId, name: framework.name })),
      ...unstarredAvailableFrameworks.map((av) => ({ standardId: av.standardId, name: av.name })),
    ],
    [frameworkCardData, scoredFrameworkTiles, unstarredAvailableFrameworks],
  )

  // Per-workload waived counts. The heatmap surfaces both — "active" is the
  // protected count, "expired" is the count of waivers past their `expiresAt`
  // and currently not protecting their finding. Split out so the WAIVED cell
  // can render the total with an "{n} expired" hint, and the click handler
  // can route to the expired view when there's something to look at.
  const waivedCountByWorkload = useMemo(() => {
    const counts: Record<M365Workload, { active: number; expired: number }> = {
      Identity: { active: 0, expired: 0 },
      Exchange: { active: 0, expired: 0 },
      Teams: { active: 0, expired: 0 },
      SharePoint: { active: 0, expired: 0 },
      Defender: { active: 0, expired: 0 },
      Other: { active: 0, expired: 0 },
    }
    for (const v of violations) {
      if (v.status !== 'Waived') continue
      const w = getWorkloadForProduct(v.product || v.category)
      if (v.waiver?.isExpired) counts[w].expired += 1
      else counts[w].active += 1
    }
    return counts
  }, [violations])

  // Phase C §6.3 — Workload heatmap rows.
  //
  // Counts are bucketed client-side from the `violations` list, which the
  // dashboard hook already constrains to starred frameworks (the violations
  // endpoint defaults to onlyStarred=true on the server — see
  // StandardsComplianceController.cs:602). This keeps the heatmap scoped to
  // frameworks contributing to the Guardian Score, matching the score tile,
  // pillar subtitle, and the framework tiles.
  //
  // We intentionally don't use `dashboardData.workloadScores` here even when
  // available: that server aggregation includes every assigned framework
  // regardless of star state, so its openShall/openShould/openMay counts
  // and per-workload health scores would over-report relative to the rest of
  // the page. Bringing back the Health column requires a server-side
  // `?onlyStarred=true` on the scores endpoint — tracked as a follow-up.
  const workloadHeatmapRows = useMemo<HeatmapRow[]>(() => {
    const buckets: Record<M365Workload, { shall: number; should: number; may: number; total: number }> = {
      Identity: { shall: 0, should: 0, may: 0, total: 0 },
      Exchange: { shall: 0, should: 0, may: 0, total: 0 },
      Teams: { shall: 0, should: 0, may: 0, total: 0 },
      SharePoint: { shall: 0, should: 0, may: 0, total: 0 },
      Defender: { shall: 0, should: 0, may: 0, total: 0 },
      Other: { shall: 0, should: 0, may: 0, total: 0 },
    }
    for (const v of violations) {
      if (v.status !== 'Open') continue
      const w = getWorkloadForProduct(v.product || v.category)
      const bucket = buckets[w]
      bucket.total += 1
      if (v.criticality === 'SHALL') bucket.shall += 1
      else if (v.criticality === 'SHOULD') bucket.should += 1
      else bucket.may += 1
    }
    return (Object.keys(buckets) as M365Workload[])
      .filter(w => w !== 'Other' || buckets[w].total > 0)
      .map(w => {
        const b = buckets[w]
        const waived = waivedCountByWorkload[w]
        // The waived column carries two axes — total waivers (numeric, for the
        // cell click affordance the heatmap derives from a positive number)
        // and the expired sub-count, surfaced on `hint`. Keeping `value`
        // numeric preserves clickability; the column header names both.
        const waivedTotal = waived.active + waived.expired
        return {
          label: w,
          sublabel: `${b.total} open`,
          cells: [
            { value: b.shall, intensity: intensityFromShall(b.shall), crit: b.shall > 0 },
            { value: b.should, intensity: intensityFromShould(b.should) },
            { value: b.may, intensity: intensityFromMay(b.may) },
            {
              value: waivedTotal,
              intensity: 'l0' as Intensity,
              hint: waived.expired > 0 ? `${waived.expired} expired` : undefined,
            },
          ],
        }
      })
  }, [violations, waivedCountByWorkload])

  // Phase B §6.4 — 30-day composite trend KPIs. Computed server-side data
  // would be cleaner, but the trend array is already on the client and the
  // math is trivial.
  const trend30dKpis = useMemo(() => {
    const points = scoreHistory30d ?? []
    if (points.length === 0) return null
    const sorted = [...points].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    )
    const scores = sorted.map(p => p.overallScore)
    const first = scores[0]
    const last = scores[scores.length - 1]
    return {
      points: sorted,
      net: Math.round((last - first) * 10) / 10,
      best: Math.round(Math.max(...scores) * 10) / 10,
      worst: Math.round(Math.min(...scores) * 10) / 10,
      avg: Math.round((scores.reduce((s, n) => s + n, 0) / scores.length) * 10) / 10,
    }
  }, [scoreHistory30d])

  // The 30-day series for TrendChart: one point per day, labelled "Sep 5", in
  // date order (xDomain keeps the order the labels can't carry on their own).
  const trendSeries = useMemo(() => {
    if (!trend30dKpis) return null
    const label = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    const points = trend30dKpis.points.map(p => ({ x: label(p.date), y: Math.round(p.overallScore * 10) / 10 }))
    return { points, xDomain: points.map(p => p.x) }
  }, [trend30dKpis])

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        refetchAll()
        refetchControlChanges()
      },
    }),
    [refetchAll, refetchControlChanges],
  )

  // Handle waiver creation
  const handleAddWaiver = (violation: ComplianceViolation) => {
    setSelectedViolation(violation)
    setIsWaiverModalOpen(true)
  }

  // Handle waiver edit
  const handleEditWaiver = (violation: ComplianceViolation) => {
    setSelectedViolation(violation)
    setIsWaiverModalOpen(true)
  }

  const handleSubmitWaiver = async (request: CreateWaiverRequest) => {
    if (!tenantId) return

    try {
      await createWaiverMutation.mutateAsync({
        tenantId,
        data: request,
      })
      setIsWaiverModalOpen(false)
      setSelectedViolation(null)
      toast.success('Waiver added.')
    } catch (error) {
      console.error('Failed to create waiver:', error)
      toast.error(
        getServerMessage(error, "Couldn't save that waiver. Guardian will retry shortly."),
      )
    }
  }

  const handleUpdateWaiver = async (waiverId: string, request: UpdateWaiverRequest) => {
    if (!tenantId) return

    try {
      await updateWaiverMutation.mutateAsync({
        tenantId,
        waiverId,
        data: request,
      })
      setIsWaiverModalOpen(false)
      setSelectedViolation(null)
      toast.success('Waiver updated.')
    } catch (error) {
      console.error('Failed to update waiver:', error)
      toast.error(
        getServerMessage(error, "Couldn't update that waiver. Give Guardian a moment and try again."),
      )
    }
  }

  const handleDeleteWaiver = async (violation: ComplianceViolation) => {
    if (!tenantId || !violation.waiver) return

    if (!window.confirm('Remove this waiver? The policy returns to Open status, which affects your compliance score.')) return

    try {
      await deleteWaiverMutation.mutateAsync({
        tenantId,
        waiverId: violation.waiver.id,
      })
      toast.success('Waiver removed.')
    } catch (error) {
      console.error('Failed to delete waiver:', error)
      toast.error(
        getServerMessage(error, "Couldn't remove that waiver. Guardian will hold off and retry."),
      )
    }
  }

  // Handle standards config save
  const handleSaveConfig = async (updates: UpdateStandardConfigRequest[]) => {
    if (!tenantId) return

    try {
      await bulkUpdateStandardsMutation.mutateAsync({
        tenantId,
        updates,
      })
      setIsConfigModalOpen(false)
      toast.success('Configuration saved.')
    } catch (error) {
      console.error('Failed to update standards config:', error)
      toast.error("Couldn't save the configuration. Try again in a moment.")
    }
  }

  // Handle framework star toggle (include in Guardian score)
  const handleToggleIncludeInScore = async (framework: FrameworkScoreDisplay) => {
    if (!tenantId) return

    try {
      await toggleIncludeInScoreMutation.mutateAsync({
        tenantId,
        standardId: framework.standardId,
        includeInGuardianScore: !framework.includeInGuardianScore,
      })
      // Optimistic update already handled by the mutation hook
    } catch (error) {
      console.error('Failed to toggle include in score:', error)
      toast.error("Couldn't update that framework. Try again in a moment.")
    }
  }

  // One-shot "Enable" for the DisabledFrameworkTile / disabled deep-dive row.
  // Enabling must do BOTH: turn the framework on for evaluation (IsEnabled) AND
  // include it in the Guardian Score. The old behavior only set
  // IncludeInGuardianScore=true while leaving IsEnabled untouched — on a tenant
  // whose framework was IsEnabled=false that meant it was never evaluated, so
  // the tile stayed empty and the button looked like it "did nothing." We no
  // longer kick an inline re-evaluation here — ad-hoc evaluation is a Platform
  // Admin action now (scan-trigger removal, 2026-06-23), and Guardian's automatic
  // 2x/day sweep evaluates the newly enabled framework. Tracks the in-flight
  // standardId locally so only the clicked tile shows "Enabling".
  const handleEnableFramework = async (standardId: string) => {
    if (!tenantId) return
    setEnablingStandardId(standardId)
    try {
      const current = assignments.find((a) => a.standardId === standardId)
      await bulkUpdateStandardsMutation.mutateAsync({
        tenantId,
        updates: [{
          standardId,
          isEnabled: true,
          weight: current?.weight ?? 100,
          includeInGuardianScore: true,
        }],
      })
      toast.success('Framework enabled. Guardian evaluates it on the next assessment.')
    } catch (error) {
      console.error('Failed to enable framework:', error)
      toast.error(getServerMessage(error, "Couldn't enable that framework. Guardian will retry shortly."))
    } finally {
      setEnablingStandardId(null)
    }
  }

  // Handle framework details view - switch to By Standard and scroll to the framework
  const handleViewFrameworkDetails = useCallback((framework: FrameworkScoreDisplay) => {
    setActiveInternalTab('by-standard')
    // Scroll to the framework section after the view renders
    scrollToId(`framework-${framework.code}`)
  }, [scrollToId])

  // Heatmap → deep-dive handoff.
  //   col 0/1/2 (Critical/High/Medium) → By workload, scrolls to that
  //   workload's article and opens the matching severity bucket.
  //   col 3 (Waived) → the failing-controls table, filtered by workload +
  //   status, and scrolls to it.
  // Zero-value cells are non-interactive in EditorialHeatmap, so empty cells
  // never reach this handler.
  const handleHeatmapCellClick = useCallback(
    (_rowIdx: number, colIdx: number, row: { label: string }) => {
      const workload = row.label as M365Workload

      if (colIdx === 3) {
        setSelectedWorkloadFilter(workload)
        setViolationsTableSignal(prev => ({ status: 'Waived', tick: prev.tick + 1 }))
        scrollToId('controls')
        return
      }

      const groupForCol: Record<number, 'critical' | 'high' | 'medium'> = {
        0: 'critical',
        1: 'high',
        2: 'medium',
      }
      const group = groupForCol[colIdx]
      if (!group) return
      setActiveInternalTab('by-workload')
      setOpenWorkloadGroup(prev => ({ workload, group, tick: prev.tick + 1 }))
      scrollToId(`workload-${workload}`)
    },
    [scrollToId],
  )

  // Focus one policy's row in the failing-controls table (it clears the
  // table's filters, opens the row and scrolls to it).
  const focusPolicy = useCallback((policyCode: string) => {
    setSelectedWorkloadFilter('all')
    setFocusViolationCode(policyCode)
  }, [])

  // In-page hash targets (the brief's "Fix …" button goes to #controls). The
  // router doesn't scroll to a hash, so do it once the sections exist, and
  // again on each navigation to it (`key` changes on a repeat click).
  const { hash, key: locationKey } = useLocation()
  const hasData = !!summary && !!dashboardData
  useEffect(() => {
    const id = hash.slice(1)
    if (!id || !hasData) return
    scrollToId(id)
  }, [hash, locationKey, hasData, scrollToId])

  // `?workload=` / `?view=violations` deep-link scroll, deferred until the
  // sections exist (same gate as the hash scroll above).
  useEffect(() => {
    if (!pendingScrollId || !hasData) return
    scrollToId(pendingScrollId)
    setPendingScrollId(null)
  }, [pendingScrollId, hasData, scrollToId])

  // Group violations by workload (for By Workload tab)
  const violationsByWorkload = useMemo(() => {
    const grouped: Record<M365Workload, ComplianceViolation[]> = {
      Identity: [],
      Exchange: [],
      Teams: [],
      SharePoint: [],
      Defender: [],
      Other: [],
    }
    violations.forEach((v) => {
      const workload = getWorkloadForProduct(v.product || v.category)
      grouped[workload].push(v)
    })
    return grouped
  }, [violations])

  // Pass-through of every policy status so ViolationsTable can drive its own
  // Failing / Passing / All view. The workload filter still applies.
  const filteredViolations = useMemo(() => {
    if (selectedWorkloadFilter === 'all') return violations
    return violations.filter((v) => {
      const workload = getWorkloadForProduct(v.product || v.category)
      return workload === selectedWorkloadFilter
    })
  }, [violations, selectedWorkloadFilter])

  // Get external frameworks (exclude Guardian internal framework), sorted by FRAMEWORK_ORDER
  const externalFrameworks = useMemo(() => {
    if (!dashboardData?.frameworks) {
      return []
    }

    return dashboardData.frameworks
      .filter((f) => !f.isGuardian)
      .sort((a, b) => {
        const orderA = FRAMEWORK_ORDER[a.code] ?? 99
        const orderB = FRAMEWORK_ORDER[b.code] ?? 99
        return orderA - orderB
      })
  }, [dashboardData?.frameworks])

  // The workloads the checked controls span, for the table's description.
  const workloadCount = useMemo(
    () => new Set(violations.map(v => getWorkloadForProduct(v.product || v.category))).size,
    [violations],
  )

  const configureButton = (
    <Button kind="tertiary" icon={<IconSliders />} onClick={() => setIsConfigModalOpen(true)}>
      Configure frameworks
    </Button>
  )

  // Render loading state — skeleton in the final layout's shape (brief, then
  // the framework tiles and the controls table), not a spinner.
  if (isLoading && !summary) {
    return (
      <div className="mt-8 min-w-0 space-y-8">
        <div className="max-w-[56ch] space-y-4" aria-hidden="true">
          <Skeleton width="80%" />
          <Skeleton width="60%" />
          <Skeleton lines={2} />
        </div>
        <div className="bg-layer-1 p-4">
          <Skeleton lines={5} label="Loading compliance data" />
        </div>
      </div>
    )
  }

  // Render error state — a blocking error with the cause and the recovery.
  if (isError) {
    return (
      <div className="mt-8">
        <InlineNotification
          kind="error"
          title="Guardian couldn't read the compliance register."
          actions={
            <Button
              kind="tertiary"
              size="sm"
              onClick={() => { refetchAll(); refetchControlChanges() }}
            >
              Retry now
            </Button>
          }
        >
          The data didn&rsquo;t load. This is usually temporary. Retry now, or check back after the next
          evaluation.
        </InlineNotification>
      </div>
    )
  }

  // The status line under the brief: the all-framework lens, the last run's
  // duration and the collection health. Helper text; the glyph and the word
  // carry each state (no hue).
  const unhealthyCount = (collectionHealth ?? []).filter(h => hasIncompleteData(h)).length
  const statusItems: React.ReactNode[] = []
  if (summary) {
    // All-framework lens — the score tile reads only the ★ frameworks in the
    // Guardian Score; this surfaces the average across every assigned
    // framework so the broader number isn't lost. Shown only when an
    // unstarred framework actually pulls the two apart.
    if (unstarredFrameworkCount > 0 && summary.overallScore != null) {
      statusItems.push(
        <span key="all">
          All frameworks <b className="font-semibold tabular-nums text-text-primary">{Math.round(summary.overallScore)}%</b>,
          including {unstarredFrameworkCount} outside your Guardian Score
        </span>,
      )
    }
    if (summary.evaluationDurationMs) {
      statusItems.push(
        <span key="duration" className="tabular-nums">
          Last evaluation took {(summary.evaluationDurationMs / 1000).toFixed(1)}s
        </span>,
      )
    }
    if (collectionHealth && collectionHealth.length > 0) {
      statusItems.push(
        unhealthyCount === 0 ? (
          <span key="health" className="inline-flex items-center gap-1.5">
            <IconCheck size={12} />
            All workload data collected
          </span>
        ) : (
          <span key="health" className="inline-flex items-center gap-1.5 text-text-primary">
            <IconWarning size={12} />
            {unhealthyCount} workload{unhealthyCount > 1 ? 's' : ''} with incomplete data
          </span>
        ),
      )
    }
  }

  return (
    <div className="min-w-0">
      <PillarBrief
        brief={brief}
        score={starredBpaScore}
        components={[]}
        tone="bpa"
        isLoading={isLoading}
        scoreLabel="Best Practice"
        provenance={assessmentProvenance({ at: summary?.lastEvaluatedAt, controls: dashboardData?.totalControls })}
        // Mockup 03 reads "Weighted by severity", but the score weights each
        // control by its curated policy weight (StandardsComplianceService
        // WeightedScore), not by severity.
        scoreMeta={starredBpaScore != null ? 'Weighted by control impact' : undefined}
        secondaryAction={configureButton}
      />

      {/* No data state — a verdict, not a void. */}
      {!summary && (
        <div className="mt-8 bg-layer-1 px-4">
          <EmptyState
            icon={ChartBarIcon}
            headline="Nothing to report yet."
            body="Guardian evaluates your tenant against CISA SCuBA, NIST SP 800-53, MCSB and EIDSCA twice a day. Results show here after the first scheduled evaluation."
          />
        </div>
      )}

      {/* Main content - only show if we have data */}
      {summary && dashboardData && (
        <>
          {/* Framework tiles (mockup 03 `.fws`), straight under the brief: one
              per framework in the Guardian Score, up to four a row, then the
              frameworks outside the score as one strip. */}
          <section id="frameworks" aria-label="Frameworks" className={clsx('mt-8', SCROLL_MT_TOP)}>
            {scoredFrameworkTiles.length > 0 && (
              <CondensedGrid
                as="ul"
                columns={{
                  base: 1,
                  'c-md': Math.min(scoredFrameworkTiles.length, 2) as GridColumnCount,
                  'c-xlg': wideTileColumns(scoredFrameworkTiles.length),
                }}
              >
                {scoredFrameworkTiles.map(({ framework, delta7d, sparkline }) => (
                  <FrameworkTile
                    key={framework.standardId}
                    framework={framework}
                    delta7d={delta7d}
                    sparkline={sparkline}
                    onToggleStar={() => handleToggleIncludeInScore(framework)}
                    onOpen={() => handleViewFrameworkDetails(framework)}
                  />
                ))}
              </CondensedGrid>
            )}
            {outsideScoreFrameworks.length > 0 && (
              <OutsideScoreStrip
                frameworks={outsideScoreFrameworks}
                attached={scoredFrameworkTiles.length > 0}
                enablingId={enablingStandardId}
                onEnable={handleEnableFramework}
              />
            )}
            {/* The tiles' notes: the all-framework lens, the last run and
                collection health, then what the checks are. */}
            {statusItems.length > 0 && (
              <p className="m-0 mt-3 flex flex-wrap gap-x-6 gap-y-1 text-helper text-text-helper">{statusItems}</p>
            )}
            <p className={clsx('m-0 max-w-[96ch] text-helper text-text-helper', statusItems.length > 0 ? 'mt-1' : 'mt-3')}>
              Built-in checks derived from CISA SCuBA, NIST SP 800-53, Microsoft MCSB and EIDSCA. MCSB is always in your
              Guardian Score; star a framework to add it. For a full compliance assessment, pair Guardian with Microsoft
              Purview Compliance Manager.
            </p>
          </section>

          {/* Failing controls — the DataTable the brief's primary action opens. */}
          <section id="controls" className={clsx('mt-12', SCROLL_MT_TOP)}>
            <ViolationsTable
              key={`status-${violationsTableSignal.status}-${violationsTableSignal.tick}`}
              violations={filteredViolations}
              onAddWaiver={handleAddWaiver}
              onEditWaiver={handleEditWaiver}
              onDeleteWaiver={handleDeleteWaiver}
              focusPolicyCode={focusViolationCode}
              onFocusConsumed={handleFocusConsumed}
              defaultStatusFilter={violationsTableSignal.status}
              description={
                // Checked and passing are separate counts (waived and no-data
                // controls sit outside both), so the line never adds them up (§7 X5).
                `${dashboardData.totalControls} controls checked across ${workloadCount} workload${workloadCount === 1 ? '' : 's'}. ${dashboardData.passedControls} pass.`
              }
              toolbarStart={
                <TableSelect
                  label="Workload"
                  value={selectedWorkloadFilter}
                  onChange={(w) => setSelectedWorkloadFilter(w as M365Workload | 'all')}
                  options={[
                    { value: 'all', label: 'All' },
                    // Short labels: a select is as wide as its longest option, and the
                    // toolbar has to fit a phone. Incomplete collection shows in the
                    // status line above and in the By workload view.
                    ...M365_WORKLOADS.filter((w) => w !== 'Other').map((workload) => ({
                      value: workload,
                      label: `${workload} (${violationsByWorkload[workload].filter((v) => v.status === 'Open').length})`,
                    })),
                  ]}
                />
              }
            />
          </section>

          {/* Sticky in-page nav for the sections below the table (mockup 03
              ends at the table, so the bar starts here); the deep-dive item is
              the pivot (trailing arrow). */}
          <div className="mt-12">
            <SectionJumpBar
              sections={[
                { id: 'top-six', label: 'Top six', badge: Math.min(filteredBpaPriorityActions.length, 6) },
                ...(workloadHeatmapRows.length > 0 ? [{ id: 'workloads', label: 'Workloads' }] : []),
                ...(trend30dKpis && trendSeries ? [{ id: 'trend', label: '30-day trend' }] : []),
                { id: 'moves', label: 'Recent moves', badge: windowedControlChanges?.length ?? 0 },
                { id: 'deep-dive', label: 'Deep dive', accent: 'deep' as const },
              ]}
              summary={
                <span className="flex items-baseline gap-2">
                  <span>Best Practice</span>
                  <b className="font-semibold tabular-nums text-text-primary">
                    {starredBpaScore != null ? `${Math.round(starredBpaScore)}%` : 'Not scored'}
                  </b>
                </span>
              }
            />
          </div>

          {/* ============================================================== */}
          {/* Phase B §6.1 — Top six to fix first                              */}
          {/* ============================================================== */}
          <section id="top-six" className={clsx('mt-8', SCROLL_MT)}>
            <SectionHead id="top-six-h" title="Top six to fix first.">
              Critical findings across the frameworks in your Guardian Score, ranked by impact.
            </SectionHead>

            {/* Framework filter. Pure client-side filter on action.id. */}
            {availableBpaFrameworkChips.length > 1 && (
              <div role="group" aria-label="Filter the top six by framework" className="mb-4 flex flex-wrap items-center gap-2">
                {availableBpaFrameworkChips.map(code => (
                  <Tag key={code} selected={bpaFrameworkFilter === code} onClick={() => setBpaFrameworkFilter(code)}>
                    {code === 'all' ? 'All frameworks' : code}
                  </Tag>
                ))}
              </div>
            )}

            <PriorityQueue
              actions={filteredBpaPriorityActions}
              ariaLabel="Top six to fix first"
              emptyState={
                bpaFrameworkFilter === 'all'
                  ? bpaPriorityActionsLoading
                    ? 'Still gathering the priority queue.'
                    : 'Nothing to review. No critical findings in the frameworks in your Guardian Score.'
                  : `No critical findings in ${bpaFrameworkFilter}. Pick another framework to see its items.`
              }
              onItemClick={(action) => {
                const policyCode = action.id.startsWith('bpa:') ? action.id.slice(4) : null
                if (!policyCode) return
                const matchingViolation = violations.find((v) => v.policyCode === policyCode)
                if (!matchingViolation) {
                  toast(`No matching finding for ${policyCode}. It may have been resolved.`)
                  return
                }
                focusPolicy(policyCode)
              }}
            />
          </section>

          {/* ============================================================== */}
          {/* Phase C §6.3 — Workload heatmap                                  */}
          {/* ============================================================== */}
          {workloadHeatmapRows.length > 0 && (
            <section id="workloads" className={clsx('mt-12', SCROLL_MT)}>
              <SectionHead id="workloads-h" title="Open findings by workload and severity.">
                Scoped to the frameworks in your Guardian Score. Select a count to open it.
              </SectionHead>
              <EditorialHeatmap
                rows={workloadHeatmapRows}
                columns={['Critical', 'High', 'Medium', 'Waived']}
                labelHeader="Workload"
                ariaLabel="Open findings by workload and severity"
                onCellClick={handleHeatmapCellClick}
              />
            </section>
          )}

          {/* ============================================================== */}
          {/* Phase B §6.4 — 30-day composite trend                            */}
          {/* ============================================================== */}
          {trend30dKpis && trendSeries && (
            <section id="trend" className={clsx('mt-12', SCROLL_MT)}>
              <SectionHead id="trend-h" title="The Best Practice score over 30 days.">
                The Best Practice score, one point per day. The tiles below give the net change, the best, the worst and the average.
              </SectionHead>
              {trendSeries.points.length >= 2 ? (
                <Tile padding={16}>
                  <TrendChart
                    series={[{ id: 'bpa', label: 'Best Practice', category: 'bpa', points: trendSeries.points }]}
                    xDomain={trendSeries.xDomain}
                    label="30-day Best Practice score trend"
                  />
                </Tile>
              ) : (
                <p className="m-0 bg-layer-1 px-4 py-8 text-center text-helper text-text-helper">
                  Two or more days of snapshots are needed to chart a trend.
                </p>
              )}
              <CondensedGrid as="ul" columns={{ base: 2, 'c-md': 4 }} className="mt-px border-t-0">
                <TrendKpi label="Net change, 30 days" value={formatSignedDelta(trend30dKpis.net)} />
                <TrendKpi label="Best, 30 days" value={`${trend30dKpis.best}`} />
                <TrendKpi label="Worst, 30 days" value={`${trend30dKpis.worst}`} />
                <TrendKpi label="Average, 30 days" value={`${trend30dKpis.avg}`} />
              </CondensedGrid>
            </section>
          )}

          {/* ============================================================== */}
          {/* §6.5 — Recent control moves feed                                 */}
          {/* One row per policy that flipped Pass↔Fail or was waived since    */}
          {/* the prior eval. Server pre-computes the signed GS delta per      */}
          {/* row; consumers must not re-sort. Section renders unconditionally */}
          {/* — error, empty, and populated states each get their own panel.  */}
          {/* See .agents/Tasks/High Priority/per-control-change-feed.md.     */}
          {/* ============================================================== */}
          <section id="moves" className={clsx('mt-12', SCROLL_MT)}>
            <SectionHead id="moves-h" title={`What moved the score in the last ${MOVES_WINDOW_DAYS} days.`}>
              Single controls that flipped in that time, scored for impact.
            </SectionHead>
            {controlChangesError ? (
              <InlineNotification
                kind="info"
                title="Recent control moves couldn't be loaded."
                actions={
                  <Button kind="ghost" size="sm" onClick={() => refetchControlChanges()}>
                    Try again
                  </Button>
                }
              />
            ) : windowedControlChanges && windowedControlChanges.length === 0 ? (
              <div className="bg-layer-1 px-4">
                <EmptyState
                  headline={`Nothing moved in the last ${MOVES_WINDOW_DAYS} days.`}
                  body="Flips show here after a policy changes in your tenant and the next evaluation runs."
                />
              </div>
            ) : !windowedControlChanges ? null : (
              <MovesTable
                changes={visibleControlChanges ?? []}
                total={windowedControlChanges.length}
                expanded={movesExpanded}
                onToggleExpanded={() => setMovesExpanded((v) => !v)}
                onActivate={(change) => {
                  const policyCode = change.policyCode
                  if (!policyCode) return
                  const matchingViolation = violations.find((v) => v.policyCode === policyCode)
                  if (!matchingViolation) {
                    const msg =
                      change.changeKind === 'Waived' || change.changeKind === 'WaiverModified'
                        ? `No matching finding for ${policyCode}. The waiver may be hiding it from the default view.`
                        : `No matching finding for ${policyCode}. It may have been resolved.`
                    toast(msg)
                    return
                  }
                  focusPolicy(policyCode)
                }}
              />
            )}
          </section>

          {/* ============================================================== */}
          {/* §6.6 — Deep dive                                                 */}
          {/* One section heading over line tabs; each view is a tab panel.    */}
          {/* The Violations view moved to the failing-controls table above.   */}
          {/* ============================================================== */}
          <section id="deep-dive" className={clsx('mt-12', SCROLL_MT)}>
            <SectionHead id="deep-dive-h" title="The full inventory, by standard, workload or history.">
              Pick a lens and drill in.
            </SectionHead>

            <LineTabs
              aria-label="Deep dive view"
              items={internalTabs.map(t => ({ id: t.key, label: t.name }))}
              value={activeInternalTab}
              onChange={(id) => setActiveInternalTab(id as DeepDiveKey)}
              tabIdPrefix="deep-tab-"
              panelIdPrefix="deep-panel-"
            />

            <div className="mt-6">
              {activeInternalTab === 'by-standard' && (
                <div id="deep-panel-by-standard" role="tabpanel" aria-labelledby="deep-tab-by-standard" className="space-y-6">
                  {(() => {
                    const sorted = [...(summary?.frameworkScores || [])].sort((a, b) => {
                      const orderA = FRAMEWORK_ORDER[a.code] ?? 99
                      const orderB = FRAMEWORK_ORDER[b.code] ?? 99
                      return orderA - orderB
                    })

                    return sorted.map((framework) => {
                      // Unstarred frameworks render as a muted "Enable to view" row
                      // (same id so scroll-to-framework handoffs from the tiles
                      // still resolve). Details, violations list, and controls
                      // are all hidden until the user opts in.
                      const isStarred =
                        framework.includeInGuardianScore ||
                        (framework.code ?? '').toUpperCase() === 'MCSB'
                      const displayName =
                        framework.code === 'EIDSCA' ? 'Entra ID Security Config Analyzer (EIDSCA)' : framework.name
                      if (!isStarred) {
                        return (
                          <article
                            key={framework.standardId}
                            id={`framework-${framework.code}`}
                            className={clsx('flex flex-wrap items-center justify-between gap-4 bg-layer-1 p-4', SCROLL_MT)}
                          >
                            <div className="min-w-0">
                              <p className="m-0 text-helper text-text-helper">{framework.code}</p>
                              <h3 className="m-0 mt-0.5 text-body font-semibold text-text-secondary">{displayName}</h3>
                              <p className="m-0 mt-1 text-helper text-text-helper">
                                Not in your Guardian Score. Enable it to see its findings.
                              </p>
                            </div>
                            <Button
                              kind="tertiary"
                              size="sm"
                              onClick={() => handleEnableFramework(framework.standardId)}
                              loading={enablingStandardId === framework.standardId}
                              aria-label={`Enable ${displayName}`}
                            >
                              {enablingStandardId === framework.standardId ? 'Enabling' : 'Enable'}
                            </Button>
                          </article>
                        )
                      }

                      const frameworkViolations = violations.filter(
                        (v) => (v.frameworkReferences ?? []).some(ref => refMatchesFramework(ref, framework.code))
                      )
                      const blurb = FRAMEWORK_BLURBS[framework.code]
                      const viewMode = frameworkViewMode[framework.code] ?? 'issues'

                      return (
                        <article
                          key={framework.standardId}
                          id={`framework-${framework.code}`}
                          className={clsx('min-w-0 bg-layer-1', SCROLL_MT)}
                        >
                          {/* Framework header — one score figure; the meter below
                              carries composition. */}
                          <header className="flex flex-wrap items-start justify-between gap-4 p-4">
                            <div className="min-w-0">
                              <p className="m-0 text-helper text-text-helper">{framework.code}</p>
                              <h3 className="m-0 mt-0.5 text-heading font-normal text-text-primary">{displayName}</h3>
                              {blurb && <p className="m-0 mt-1 max-w-[56ch] text-body text-text-secondary">{blurb}</p>}
                            </div>
                            <div className="flex flex-none flex-wrap items-center gap-6">
                              <ContentSwitcher
                                aria-label={`${framework.code} view`}
                                items={[
                                  { id: 'issues', label: 'Issues' },
                                  { id: 'controls', label: 'All controls' },
                                ]}
                                value={viewMode}
                                onChange={(id) =>
                                  setFrameworkViewMode(prev => ({ ...prev, [framework.code]: id as 'issues' | 'controls' }))
                                }
                              />
                              <div className="text-right">
                                <BigNumber value={Math.round(framework.score)} suffix="%" size="figure" className="justify-end" />
                                <p className="m-0 text-helper tabular-nums text-text-helper">
                                  Importance {Math.max(1, Math.min(10, Math.round(framework.weight / 10) || 5))} of 10
                                </p>
                              </div>
                            </div>
                          </header>

                          {/* Composition meter — policy checks by lifecycle.
                              Control-level counts stay on the passing segment's
                              caption (backend-authoritative). */}
                          {(() => {
                            const total = frameworkViolations.length
                            if (total === 0) {
                              // No client-matched checks to compose a meter
                              // from — keep the authoritative control count.
                              return (
                                <p className="m-0 px-4 pb-4 text-helper text-text-helper">
                                  {framework.passedControls} of {framework.totalControls} controls passing
                                </p>
                              )
                            }
                            const passing = frameworkViolations.filter(v => v.status === 'Pass' || v.status === 'Resolved').length
                            const waived = frameworkViolations.filter(v => v.status === 'Waived').length
                            const noData = frameworkViolations.filter(v => v.status === 'DataUnavailable').length
                            const failing = total - passing - waived - noData
                            return (
                              <SegmentedMeter
                                className="px-4 pb-4"
                                caption={`${total} policy checks · ${framework.passedControls} of ${framework.totalControls} controls pass`}
                                segments={[
                                  { id: 'passing', value: passing, tone: 'bpa', label: 'passing' },
                                  { id: 'failing', value: failing, tone: 'strong', label: 'failing' },
                                  { id: 'waived', value: waived, tone: 'low', label: 'waived' },
                                  { id: 'nodata', value: noData, tone: 'off', label: 'not evaluated' },
                                ]}
                              />
                            )
                          })()}

                          {/* NIST SP 800-53 specific disclaimer */}
                          {framework.code === 'NIST_800_53' && (
                            <div className="px-4 pb-4">
                              <NistDisclaimer />
                            </div>
                          )}

                          {/* Content: Issues view or All Controls view */}
                          <div className="border-t border-line-subtle">
                            {viewMode === 'controls' ? (
                              <div className="p-4">
                                <FrameworkControlsList
                                  policies={frameworkViolations}
                                  frameworkCode={framework.code}
                                  passedControls={framework.passedControls}
                                  totalControls={framework.totalControls}
                                  partialControls={framework.partialControls}
                                  onAddWaiver={handleAddWaiver} onEditWaiver={handleEditWaiver} onDeleteWaiver={handleDeleteWaiver}
                                />
                              </div>
                            ) : frameworkViolations.length > 0 ? (
                              <GroupedPoliciesDisplay
                                policies={frameworkViolations}
                                onAddWaiver={handleAddWaiver} onEditWaiver={handleEditWaiver} onDeleteWaiver={handleDeleteWaiver}
                                frameworkCode={framework.code}
                              />
                            ) : (
                              <p className="m-0 px-4 py-8 text-center text-helper text-text-helper">
                                Nothing to review for this framework.
                              </p>
                            )}
                          </div>
                        </article>
                      )
                    })
                  })()}
                </div>
              )}

              {activeInternalTab === 'by-workload' && (
                <div id="deep-panel-by-workload" role="tabpanel" aria-labelledby="deep-tab-by-workload" className="space-y-6">
                  {/* Standalone notes for workloads with missing EXPECTED data */}
                  {collectionHealth?.map((wh) => {
                    const hasMissingGraph = wh.health.graphExpected && !wh.health.graphCollected
                    const hasMissingPs = wh.health.powerShellExpected && !wh.health.powerShellCollected
                    if (!hasMissingGraph && !hasMissingPs) return null

                    return (
                      <InlineNotification key={`health-${wh.workload}`} kind="info" title={`${wh.workload}:`}>
                        {hasMissingGraph && hasMissingPs
                          ? 'Neither Graph nor PowerShell data was collected during the last evaluation. Results may be incomplete.'
                          : hasMissingPs
                            ? 'PowerShell data was not available during the last evaluation. Some policy checks may be incomplete.'
                            : 'Graph data was not available during the last evaluation. Some policy checks may be incomplete.'}
                        {wh.health.errors.length > 0 ? ` Errors: ${wh.health.errors.join('; ')}` : ''}
                      </InlineNotification>
                    )
                  })}

                  {M365_WORKLOADS.map((workload) => {
                    // Mirror the workloads heatmap: only Open + Pass items count
                    // here. Waived/Resolved/DataUnavailable are managed in the
                    // failing-controls table, so the bucket counts here line up
                    // with the heatmap cells above.
                    const actionableViolations = violationsByWorkload[workload].filter(
                      (v) => v.status === 'Open' || v.status === 'Pass',
                    )
                    const openCount = actionableViolations.filter((v) => v.status === 'Open').length
                    const display = WORKLOAD_DISPLAY[workload]
                    const workloadHealth = collectionHealth?.find(h => h.workload === workload)

                    // Skip workloads with nothing actionable to show
                    if (actionableViolations.length === 0) return null

                    const isTargeted = openWorkloadGroup.workload === workload
                    return (
                      <article
                        key={workload}
                        id={`workload-${workload}`}
                        className={clsx('min-w-0 bg-layer-1', SCROLL_MT)}
                      >
                        {/* Workload header */}
                        <header className="flex flex-wrap items-center gap-3 p-4">
                          <display.icon
                            aria-hidden="true"
                            strokeLinecap="square"
                            strokeLinejoin="miter"
                            className="h-5 w-5 flex-none text-[color:var(--icon-secondary)]"
                          />
                          <div className="min-w-0">
                            <h3 className="m-0 flex flex-wrap items-center gap-2 text-heading font-normal text-text-primary">
                              {workload}
                              {workloadHealth && <HealthBadge health={workloadHealth} />}
                            </h3>
                            <p className="m-0 text-helper text-text-helper">
                              {openCount} open finding{openCount === 1 ? '' : 's'}
                            </p>
                          </div>
                        </header>

                        {/* Workload findings - grouped by severity */}
                        <GroupedPoliciesDisplay
                          key={isTargeted ? `force-${openWorkloadGroup.group}-${openWorkloadGroup.tick}` : 'default'}
                          policies={actionableViolations}
                          onAddWaiver={handleAddWaiver} onEditWaiver={handleEditWaiver} onDeleteWaiver={handleDeleteWaiver}
                          initialOpenKey={isTargeted ? openWorkloadGroup.group ?? undefined : undefined}
                        />
                      </article>
                    )
                  })}

                  {/* Empty state for workloads */}
                  {Object.values(violationsByWorkload).every(
                    (vs) => vs.filter((v) => v.status === 'Open' || v.status === 'Pass').length === 0,
                  ) && (
                    <div className="bg-layer-1 px-4">
                      <EmptyState headline="Nothing to review." body="No open findings in any workload." />
                    </div>
                  )}
                </div>
              )}

              {/* History — the evaluation list. The trend lives in its own section. */}
              {activeInternalTab === 'history' && (
                <div id="deep-panel-history" role="tabpanel" aria-labelledby="deep-tab-history" className="bg-layer-1 px-4 pb-2 pt-4">
                  <h3 className="m-0 text-body font-semibold text-text-primary">The last ten evaluations.</h3>
                  <StructuredList
                    className="mt-3"
                    aria-label="Evaluation history"
                    empty="No evaluation history yet. The record starts with the first scheduled evaluation."
                    rows={trends.slice(0, 10).map((point, index) => ({
                      id: `${point.date}-${index}`,
                      time: new Date(point.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                      title: new Date(point.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }),
                      sub: point.frameworkScores.map((f) => `${f.code} ${f.score}%`).join(' · '),
                      tag: (
                        <Tag className="tabular-nums" title="Overall score">
                          {Math.round(point.overallScore)}%
                        </Tag>
                      ),
                    }))}
                  />
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Modals */}
      <StandardsConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        assignments={assignments}
        onSave={handleSaveConfig}
        isSaving={bulkUpdateStandardsMutation.isPending}
        frameworks={externalFrameworks}
        onToggleFrameworkStar={handleToggleIncludeInScore}
      />

      <WaiverModal
        isOpen={isWaiverModalOpen}
        onClose={() => {
          setIsWaiverModalOpen(false)
          setSelectedViolation(null)
        }}
        violation={selectedViolation}
        onSubmit={handleSubmitWaiver}
        onUpdate={handleUpdateWaiver}
        existingWaiver={selectedViolation?.hasWaiver ? selectedViolation.waiver : undefined}
        isSubmitting={createWaiverMutation.isPending || updateWaiverMutation.isPending}
      />
    </div>
  )
})

/**
 * The recent control moves as a Carbon table: when, the control and what
 * happened (with who or what detected it), and the score impact. A row opens
 * the control in the failing-controls table. Collapsed to the first rows; the
 * footer shows the rest of the window.
 */
function MovesTable({
  changes,
  total,
  expanded,
  onToggleExpanded,
  onActivate,
}: {
  changes: ControlChange[]
  total: number
  expanded: boolean
  onToggleExpanded: () => void
  onActivate: (change: ControlChange) => void
}) {
  const stampOf = (change: ControlChange) =>
    new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(change.detectedAt))
  const columns: DataColumn<ControlChange>[] = [
    {
      id: 'control',
      header: 'Control',
      primary: true,
      cell: (change) => {
        // Verb mapping per plan §10; see CONTROL_CHANGE_TRAILING_CLAUSE.
        const trailingClause = CONTROL_CHANGE_TRAILING_CLAUSE[change.changeKind]
        const actor = formatTriggeredBy(change.triggeredBy)
        const attribution = actor ? `By ${actor} · ${stampOf(change)}` : `Detected by scheduled scan · ${stampOf(change)}`
        const where = [change.category, change.product].filter(Boolean).join(' · ')
        return (
          <>
            <b className="block font-semibold text-text-primary">
              {change.policyTitle}. <span className="font-normal">{trailingClause}</span>
              {change.affectedResourceCount > 0 && (
                <span className="font-normal text-text-secondary"> ({change.affectedResourceCount} resources)</span>
              )}
            </b>
            <small className="mt-0.5 block text-helper text-text-helper">
              {where ? `${where} · ` : ''}
              {attribution}
            </small>
          </>
        )
      },
    },
    {
      id: 'impact',
      header: 'Impact',
      align: 'end',
      nowrap: true,
      // Right-column renderer: ScoreDeltaChip handles its own sign and the 0.05
      // noise threshold. `scoreDelta == null` happens for unweighted policies.
      cell: (change) => renderControlChangeRightSlot(change),
    },
  ]
  return (
    <DataTable<ControlChange>
      aria-label="Recent control moves"
      columns={columns}
      rows={changes}
      rowId={(c) => c.id}
      rowLabel={(c) => c.policyTitle}
      onRowActivate={onActivate}
      footer={
        total > MOVES_VISIBLE_CAP ? (
          <>
            <span className="tabular-nums">
              {changes.length} of {total} moves shown
            </span>
            <Button kind="ghost" size="sm" onClick={onToggleExpanded} className="-mr-3">
              {expanded ? 'Show fewer' : `Show ${total - MOVES_VISIBLE_CAP} more`}
            </Button>
          </>
        ) : undefined
      }
    />
  )
}

export default BestPracticeAnalyzerTab
